/** @format */

import {Chart} from 'components/charts/multiline-chart/model/charts';

import {ChartIds} from '../models/chartIds';
import {WIDGETS_LIST} from '../constants/widgets';
import {ALL_CHART_IDS} from '../constants/allChartIds';

export interface MultilineWithChartsOnly {
  charts: Chart<ChartIds>[];
}

/**
 * Создаст фейковый прогноз на определённую точку для recharts.
 * Это нужно, чтобы ликвидировать визуальный разрыв графика на стыке обычных значений и прогноза.
 * Учтите, что этот фейк нельзя показывать юзеру в тултипе или где-то ещё!
 * Алгоритм: если в текущей точке нет значений и есть прогноз, а в предыдущей точке нет прогноза, но есть значения, то назначаем фейковый прогноз в предыдущую точку.
 * @param multiChart
 */

const addFakeForecast = <C extends MultilineWithChartsOnly>(multiChart: C): C => {
  const newCharts = multiChart.charts.map(chart => {
    const isForecast = WIDGETS_LIST[chart.chartId]?.addForecast;
    const isFunnel = !ALL_CHART_IDS.includes(chart.chartId);

    if (!isForecast && !isFunnel) {
      return chart;
    }

    const newChartsData = [...chart.chartsData];

    for (let i = 0; i < chart.chartsData.length; i++) {
      const data = chart.chartsData[i];

      const prevData = i > 0 ? chart.chartsData[i - 1] : undefined;
      if (!prevData) {
        continue;
      }

      const isCurrentHasValues = data.primary !== undefined || data.secondary !== undefined;
      const isCurrentHasForecast =
        data.average !== undefined ||
        data.averageSecondary !== undefined ||
        data.range ||
        data.rangeSecondary;
      const isPreviousHasValues =
        prevData.primary !== undefined || prevData.secondary !== undefined;
      const isPreviousHasForecast =
        prevData.average !== undefined ||
        prevData.averageSecondary !== undefined ||
        prevData.range ||
        prevData.rangeSecondary;

      if (
        !isCurrentHasValues &&
        isCurrentHasForecast &&
        !isPreviousHasForecast &&
        isPreviousHasValues
      ) {
        // Назначаем фейковый прогноз в предыдущую точку
        // Числа должны быть такие же, как значения в этой точке, чтобы графики совпали
        const newData = {
          ...newChartsData[i - 1],
        };

        if (data.average !== undefined && newData.primary !== undefined) {
          newData.average = newData.primary;
        }

        if (data.range !== undefined && newData.primary !== undefined) {
          newData.range = [newData.primary, newData.primary];
        }

        if (data.averageSecondary !== undefined && newData.secondary !== undefined) {
          newData.averageSecondary = newData.secondary;
        }

        if (data.rangeSecondary !== undefined && newData.secondary !== undefined) {
          newData.rangeSecondary = [newData.secondary, newData.secondary];
        }

        newChartsData[i - 1] = newData;
        break; // Обязательно прерываем цикл (не может быть более 1 фейкового прогноза)
      }
    }

    return {
      ...chart,
      chartsData: newChartsData,
    };
  });

  return {
    ...multiChart,
    charts: newCharts,
  };
};

export {addFakeForecast};
